import React, { useState } from 'react';
import { string, object, func, array, bool, number } from 'prop-types';
import { propTypes, LISTING_STATE_CLOSED } from '../../../util/types';
import { FormattedMessage } from '../../../util/reactIntl';
import { createSlug } from '../../../util/urlHelpers';
import { LISTING_PAGE_PARAM_TYPE_EDIT } from '../../../util/urlHelpers';
import {
  Avatar,
  ActionButton,
  NamedLink,
  NamedRedirect,
  ExternalLink,
  Badges,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  IconMenu,
  IconVote,
  IconComment,
  IconExport,
} from '../../../components';
import { ensureUser } from '../../../util/data';
import { richText, enhanceRichTextHtml } from '../../../util/richText';
import moment from 'moment';
import classNames from 'classnames';
import ImageSlider from './ImageSlider/ImageSlider';
import Comments from './Comments/Comments';
import TranslateButton from '../../../components/TranslateButton/TranslateButton';
import css from './Post.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_DESCRIPTION = 20;
const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';
/**
 * Renders an error message when post can't be displayed
 */
const PostError = () => (
  <div className={css.post}>
    <p className={css.postError}>
      <FormattedMessage id="PostPage.error" />
    </p>
  </div>
);

/**
 * Renders the post header with author info, posting time, and menu options
 */
const PostHeader = ({
  author,
  title,
  category,
  postedAt,
  userDisplayName,
  isOwnPost,
  linkParams,
  onCloseListing,
  isRemovePostInProgress,
}) => {
  return (
    <div className={css.postInfo}>
      <div className={css.postDetails}>
        <div className={css.postTime}>
          <Avatar
            className={css.avatar}
            initialsClassName={css.avatarInitials}
            user={author}
            useUserTier
          />
          <FormattedMessage
            id="PostPage.postedBy"
            values={{
              userDisplayName,
              badges: (
                <Badges
                  className={css.badges}
                  iconClassName={css.badgeIcon}
                  imageClassName={css.badgeImage}
                  user={author}
                />
              ),
              postedAt,
              category: category?.label,
            }}
          />
        </div>
        {isOwnPost && (
          <Menu className={css.menu}>
            <MenuLabel className={css.menuLabel}>
              <IconMenu />
            </MenuLabel>
            <MenuContent className={css.menuContent}>
              <MenuItem key="ControlMenu.edit" className={css.menuItem}>
                <NamedLink className={css.menuItemButton} name="EditPostPage" params={linkParams}>
                  <FormattedMessage id="PostPage.edit" />
                </NamedLink>
              </MenuItem>
              <MenuItem key="ControlMenu.remove" className={css.menuItem}>
                <InlineTextButton
                  className={css.menuItemButton}
                  type="button"
                  onClick={onCloseListing}
                  disabled={isRemovePostInProgress}
                >
                  <FormattedMessage id="PostPage.remove" />
                </InlineTextButton>
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
      <h3 className={css.postTitle}>{title}</h3>
    </div>
  );
};

/**
 * Renders text content for a post
 */
const TextPostContent = ({ content, isHtml, description }) => {
  if (isHtml) {
    // sanitize + open external links in a new tab + lazy-load embedded images
    return (
      <div
        className={css.postHtml}
        dangerouslySetInnerHTML={{ __html: enhanceRichTextHtml(content) }}
      />
    );
  }
  return (
    <p className={css.postText}>
      {richText(description, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_DESCRIPTION,
        longWordClass: css.longWord,
      })}
    </p>
  );
};

/**
 * Renders image content for a post
 */
const ImagePostContent = ({ images, title }) => {
  return <ImageSlider images={images} title={title} />;
};

/**
 * Renders link content for a post
 */
const LinkPostContent = ({ url, preview, title }) => {
  return (
    <>
      <div className={css.linkUrl}>
        <ExternalLink href={url} className={css.linkAnchor}>
          {url}
        </ExternalLink>
      </div>

      {preview && !preview.error && (
        <div className={css.linkPreview}>
          <ExternalLink href={url} className={css.linkPreviewAnchor}>
            {preview.images?.[0] && (
              <img
                src={preview.images[0]}
                alt={preview.title || 'Link preview'}
                className={css.linkPreviewImage}
              />
            )}
            <div className={css.linkPreviewContent}>
              <span className={css.linkPreviewTitle}>{preview.title || title}</span>
              <span className={css.linkPreviewDescription}>{preview.description || url}</span>
              <span className={css.linkPreviewUrl}>{url}</span>
            </div>
          </ExternalLink>
        </div>
      )}
    </>
  );
};

/**
 * Renders action buttons for a post (vote, comment, share)
 */
const PostActions = ({
  commentsLabel,
  shareLabel,
  addPostCountError,
  voteCount,
  currentUserVote,
  onAddPostCount,
  onOpenShareModal,
}) => {
  /**
   * Scrolls to the comments section
   */
  const scrollToComments = () => {
    const commentsSection = document.getElementById('comments');
    if (!commentsSection) return;

    const offset = 71; // Topbar height - 1px border
    const elementPosition = commentsSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  return (
    <div className={css.postActions}>
      <ActionButton className={css.voteActionButtonWrapper} isButton={false}>
        <InlineTextButton
          className={classNames(css.voteActionButton, {
            [css.voteActionButtonActive]: currentUserVote === 'like',
          })}
          onClick={() => onAddPostCount('like')}
          disabled={addPostCountError}
        >
          <IconVote
            className={css.voteActionIcon}
            direction="up"
            isActive={currentUserVote === 'like'}
          />
        </InlineTextButton>
        <span className={css.voteCount}>{voteCount}</span>
        <InlineTextButton
          className={classNames(css.voteActionButton, css.dislikeVoteActionButton, {
            [css.voteActionButtonActive]: currentUserVote === 'dislike',
          })}
          onClick={() => onAddPostCount('dislike')}
          disabled={addPostCountError}
        >
          <IconVote
            className={css.voteActionIcon}
            direction="down"
            isActive={currentUserVote === 'dislike'}
          />
        </InlineTextButton>
      </ActionButton>
      <ActionButton label={commentsLabel} onClick={scrollToComments}>
        <IconComment className={css.actionIcon} />
      </ActionButton>
      <ActionButton label={shareLabel} onClick={() => onOpenShareModal()}>
        <IconExport className={css.actionIcon} />
      </ActionButton>
    </div>
  );
};

/**
 * Post component to display a single post with comments
 * Handles different post types: text, image, and link
 */
const Post = props => {
  // Destructure props with organized grouping
  const {
    // Styling
    rootClassName,
    className,

    // Context and data
    intl,
    state,
    categories,
    currentPost,
    linkPreview,
    location,
    currentUser,

    // Post loading states
    showPostInProgress,
    showPostError,
    isRemovePostInProgress,

    // Comment states
    addCommentInProgress,
    addCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    deletedCommentIds,
    currentDeleteCommentId,
    editCommentInProgress,
    editCommentError,

    // Post count states
    addPostCountInProgress,
    addPostCountError,
    voteCount,
    currentUserVote,

    // Action handlers
    onOpenShareModal,
    onAddComment,
    onDeleteComment,
    onEditComment,
    onCloseListing,
    onAddPostCount,
    onVoteComment,
    onTranslate,
    onOpenConfirmationModalWithHandlers,
    onUpdateConfirmationModalProps,
    onCloseConfirmationModal,
  } = props;

  const [translatedContent, setTranslatedContent] = useState(null);

  const classes = classNames(rootClassName || css.root, className);

  // Early returns for error states
  if (showPostError) return <PostError />;

  const hasListingState = !!currentPost.attributes.state;
  const isClosed = hasListingState && currentPost.attributes.state === LISTING_STATE_CLOSED;
  if (isClosed) return <NamedRedirect name="CommunityPage" />;

  // Extract post data
  const { title, description, publicData, createdAt } = currentPost.attributes;
  const hasImages = currentPost.images?.length > 0;
  const contentType = publicData?.contentType;
  const isTextPost = contentType === 'text';
  const isImagePost = contentType === 'image';
  const isLinkPost = contentType === 'link';
  const linkUrl = publicData?.linkUrl;

  // Author information
  const author = ensureUser(currentPost.author);
  const authorAvailable = !!currentPost?.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnPost =
    userAndListingAuthorAvailable && currentPost?.author?.id?.uuid === currentUser?.id?.uuid;

  const userLinkProps = author.id
    ? { name: 'ProfilePage', params: { id: author.id.uuid } }
    : { name: 'ProfileBasePage' };

  const authorDisplayName =
    author?.attributes?.profile?.publicData?.name?.firstName ||
    author?.attributes?.profile?.displayName;
  const currentUserDisplayName =
    currentUser?.attributes?.profile?.publicData?.name?.firstName ||
    currentUser?.attributes?.profile?.displayName;

  // Category information
  const categoryKey = publicData?.category;
  const category = categories.find(c => c.key === categoryKey);

  // Comments information - use comments from Redux state if available for real-time updates
  const commentsFromReduxState = props.commentsFromState || [];
  const comments = commentsFromReduxState.length > 0 ? commentsFromReduxState : (publicData?.comments || []);
  const commentsLength = comments?.length || 0;

  // Formatted strings
  const userDisplayName = author?.id?.uuid ? (
    <NamedLink name="ProfilePage" params={{ id: author.id.uuid }} className={css.authorLink}>
      <FormattedMessage id="CommunityPage.userDisplayName" values={{ authorDisplayName }} />
    </NamedLink>
  ) : (
    <span>
      <FormattedMessage id="CommunityPage.userDisplayName" values={{ authorDisplayName }} />
    </span>
  );

  const commentDisplayName = (
    <span className={css.highlightedText}>
      <FormattedMessage
        id="CommunityPage.userDisplayName"
        values={{ authorDisplayName: currentUserDisplayName }}
      />
    </span>
  );

  const postedAt = moment(createdAt).fromNow();
  const commentsLabel = commentsLength;
  const shareLabel = intl.formatMessage({ id: 'PostPage.actionShare' });

  // Edit link parameters for this post
  const editLinkParams = {
    id: currentPost?.id?.uuid,
    slug: createSlug(currentPost?.attributes?.title || ''),
    type: LISTING_PAGE_PARAM_TYPE_EDIT,
    tab: currentPost?.attributes?.publicData?.contentType,
  };

  return (
    <div className={classes}>
      <PostHeader
        author={author}
        title={title}
        category={category}
        postedAt={postedAt}
        userDisplayName={userDisplayName}
        isOwnPost={isOwnPost}
        linkParams={editLinkParams}
        onCloseListing={onCloseListing}
        isRemovePostInProgress={isRemovePostInProgress}
      />

      <div
        className={classNames(css.postWrapper, {
          [css.textPostWrapper]: isTextPost,
          [css.linkPostWrapper]: isLinkPost,
          [css.imagePostWrapper]: isImagePost,
        })}
      >
        {/* Render appropriate content based on post type */}
        {isTextPost && (
          <>
            <TextPostContent
              content={translatedContent || publicData.descriptionHtml}
              isHtml={!!(translatedContent || publicData.descriptionHtml)}
              description={translatedContent || description}
            />
            {(publicData.descriptionHtml || description) && (
              <TranslateButton
                html={publicData.descriptionHtml || null}
                text={!publicData.descriptionHtml ? description : null}
                onTranslate={onTranslate}
                onTranslated={translated => setTranslatedContent(translated)}
                onShowOriginal={() => setTranslatedContent(null)}
              />
            )}
          </>
        )}

        {isImagePost && hasImages && <ImagePostContent images={currentPost.images} title={title} />}

        {isLinkPost && linkUrl && (
          <LinkPostContent url={linkUrl} preview={linkPreview} title={title} />
        )}

        <PostActions
          commentsLabel={commentsLabel}
          shareLabel={shareLabel}
          addPostCountInProgress={addPostCountInProgress}
          addPostCountError={addPostCountError}
          voteCount={voteCount}
          currentUserVote={currentUserVote}
          onAddPostCount={onAddPostCount}
          onOpenShareModal={onOpenShareModal}
        />
      </div>

      {addPostCountError && (
        <p className={css.error}>
          <FormattedMessage id="PostPage.errorAddingPostCount" />
        </p>
      )}

      <Comments
        intl={intl}
        state={state}
        comments={comments}
        currentUser={currentUser}
        onAddComment={onAddComment}
        onDeleteComment={onDeleteComment}
        onEditComment={onEditComment}
        onVoteComment={onVoteComment}
        postId={currentPost.id?.uuid}
        addCommentInProgress={addCommentInProgress}
        addCommentError={addCommentError}
        deleteCommentInProgress={deleteCommentInProgress}
        deleteCommentError={deleteCommentError}
        deletedCommentIds={deletedCommentIds}
        currentDeleteCommentId={currentDeleteCommentId}
        editCommentInProgress={editCommentInProgress}
        editCommentError={editCommentError}
        commentDisplayName={commentDisplayName}
        showPostInProgress={showPostInProgress}
        location={location}
        onOpenConfirmationModalWithHandlers={onOpenConfirmationModalWithHandlers}
        onUpdateConfirmationModalProps={onUpdateConfirmationModalProps}
        onCloseConfirmationModal={onCloseConfirmationModal}
      />
    </div>
  );
};

Post.defaultProps = {
  rootClassName: null,
  className: null,
  linkPreview: null,
  currentUser: null,
  currentDeleteCommentId: null,
  showPostError: null,
  addCommentError: null,
  deleteCommentError: null,
  addPostCountError: null,
  currentUserVote: null,
  deletedCommentIds: [],
  commentsFromState: [],
};

Post.propTypes = {
  // Styling props
  rootClassName: string,
  className: string,

  // Context and data props
  intl: object.isRequired,
  state: object.isRequired,
  categories: array.isRequired,
  currentPost: object.isRequired,
  linkPreview: object,
  location: object.isRequired,
  currentUser: propTypes.currentUser,

  // Post loading props
  showPostInProgress: bool.isRequired,
  showPostError: propTypes.error,
  isRemovePostInProgress: bool.isRequired,

  // Comment state props
  addCommentInProgress: bool.isRequired,
  addCommentError: propTypes.error,
  deleteCommentInProgress: bool.isRequired,
  deleteCommentError: propTypes.error,
  deletedCommentIds: array,
  currentDeleteCommentId: string,

  // Post count props
  addPostCountInProgress: bool.isRequired,
  addPostCountError: propTypes.error,
  voteCount: number.isRequired,
  currentUserVote: string,

  // Action handlers
  onOpenShareModal: func.isRequired,
  onAddComment: func.isRequired,
  onDeleteComment: func.isRequired,
  onCloseListing: func.isRequired,
  onAddPostCount: func.isRequired,
  onVoteComment: func.isRequired,
  onManageDisableScrolling: func,
  
  // Real-time comment updates
  commentsFromState: array,
};

export default Post;
